import React from "react";
import { TopNav } from "@/components/TopNav";
import { Footer } from "@/components/Footer";
import { SEO } from "@/components/SEO";
import { ShieldCheck, Globe, Phone, MapPin, Lock, Award, Diamond, Search, FlaskConical } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { pageTr } from "@/i18n/pageTranslations";
import { useNavigate } from "react-router-dom";

const LABS = [
  { n: 1, name: "GIA – Carlsbad", country: "ÉTATS-UNIS / USA", addr: "5345 Armada Drive\nCarlsbad, CA 92008 – USA", phone: "+1 760 603 4500", web: "www.gia.edu", desc: "Référence mondiale pour l'expertise des diamants et pierres de couleur. / World reference for diamond and colored stone expertise." },
  { n: 2, name: "GIA – New York", country: "ÉTATS-UNIS / USA", addr: "580 Fifth Avenue, 27th Floor\nNew York, NY 10036 – USA", phone: "+1 212 221 5858", web: "www.gia.edu", desc: "Laboratoire principal pour les diamants et pierres de couleur. / Main lab for diamonds and colored stones." },
  { n: 3, name: "IGI – Antwerp", country: "BELGIQUE / BELGIUM", addr: "Schupstraat 1\n2018 Antwerp – Belgique", phone: "+32 3 401 08 88", web: "www.igi.org", desc: "Laboratoire historique d'IGI, référence internationale pour tous types de pierres. / IGI historic laboratory, international reference." },
  { n: 4, name: "SSEF – Basel", country: "SUISSE / SWITZERLAND", addr: "Aeschengraben 26\nCH-4051 Basel – Suisse", phone: "+41 61 262 06 40", web: "www.ssef.ch", desc: "Institut suisse indépendant, spécialisé dans les pierres de couleur et les perles. / Swiss independent institute for colored stones and pearls." },
  { n: 5, name: "Gübelin Gem Lab – Lucerne", country: "SUISSE / SWITZERLAND", addr: "Maihofstrasse 102\n6006 Lucerne – Suisse", phone: "+41 41 429 17 17", web: "www.gubelingemlab.com", desc: "Leader mondial pour les rapports d'origine géographique et l'expertise des pierres de couleur. / World leader in geographic origin reports." },
  { n: 6, name: "GIA – Bangkok", country: "THAÏLANDE / THAILAND", addr: "944 Rama IV Road\nMirtown Office Tower, 19th Floor\nBangkok 10330 – Thaïlande", phone: "+66 2 460 6945", web: "www.gia.edu", desc: "Centre de référence de GIA en Asie pour les pierres de couleur. / GIA Asia reference center." },
  { n: 7, name: "IGI – Bangkok", country: "THAÏLANDE / THAILAND", addr: "943 Silom Road\nBangrak, Bangkok 10500 – Thaïlande", phone: "+66 2 267 0077", web: "www.igi.org", desc: "Laboratoire IGI pour l'Asie du Sud-Est. / IGI South-East Asia lab." },
  { n: 8, name: "IGI – Mumbai", country: "INDE / INDIA", addr: "202, Star City (Off Western Express Hwy)\nAndheri (West), Mumbai 400053 – Inde", phone: "+91 22 4035 2550", web: "www.igi.org", desc: "Laboratoire de référence en Inde. / Reference lab in India." },
  { n: 9, name: "IGI – Dubai", country: "ÉMIRATS ARABES UNIS / UAE", addr: "DMCC, Almas Tower, Unit 510\nJumeirah Lakes Towers, Dubai – UAE", phone: "+971 4 117 3556", web: "www.igi.org", desc: "Laboratoire IGI pour le Moyen-Orient. / IGI Middle East lab." },
];

const OTHERS = [
  { name: "GIA – Hong Kong", loc: "Hong Kong", addr: "11/F, Tower B, Southmark\n11 Yip Hing Street, Wong Chuk Hang", phone: "+852 3127 8166", web: "www.gia.edu" },
  { name: "AIGS Laboratory", loc: "Thaïlande", addr: "919/1 Jewelry Trade Center\n4th Floor, Silom Road\nBangkok 10500", phone: "+66 2 637 6199", web: "www.aigslab.com" },
  { name: "GRS Gemresearch", loc: "Suisse", addr: "Chemin du Pont-du-Centenaire 109\n1228 Plan-les-Ouates\nGenève", phone: "+41 22 812 00 12", web: "www.gemresearch.ch" },
  { name: "Lotus Gemology", loc: "Thaïlande", addr: "8th Floor, G Tower\n9 Rama 9 Road, Huai Khwang\nBangkok 10310", phone: "+66 2 117 3616", web: "www.lotusgemology.com" },
];

const LabsPage = () => {
  const { lang } = useLang();
  const t = pageTr[lang].labs;
  const nav = useNavigate();
  const WHY_ICONS = [ShieldCheck, Diamond, Search, Award];
  const SPEC_ICONS = [Diamond, Diamond, MapPin, FlaskConical];

  return (
    <div className="min-h-screen bg-[#050811] text-[#F8F5EE] flex flex-col">
      <SEO
        title={t.title}
        path="/labs"
        noindex
        description={lang === "fr"
          ? "GEM PRICE — Annuaire des laboratoires gemmologiques (GIA, IGI, Gübelin, SSEF, AGL) au service des tarifs certifiés des pierres précieuses."
          : "GEM PRICE — Directory of gemmological laboratories (GIA, IGI, Gübelin, SSEF, AGL) supporting certified precious gemstone pricing."}
      />
      <TopNav />
      <div className="max-w-7xl mx-auto px-6 md:px-14 py-10 space-y-8">
        {/* Header */}
        <div className="grid md:grid-cols-3 gap-6">
          <div className="flex items-start gap-3" data-testid="lab-public-badge">
            <Globe size={22} className="text-[#C1A661] shrink-0 mt-0.5" strokeWidth={1.3} />
            <div>
              <div className="text-[10px] tracking-[0.25em] uppercase text-[#C1A661] font-medium">{t.publicBadge}</div>
              <p className="text-[11px] text-[#A1A7B3] font-light mt-1">Un contenu fiable et utile pour tous les passionnés.</p>
            </div>
          </div>
          <div className="text-center">
            <h1 className="font-serif text-4xl md:text-5xl font-light tracking-[0.15em] text-[#E2CFA4]">GEM PRICE</h1>
            <p className="mt-2 text-[13px] text-[#F8F5EE] font-light tracking-wide">{t.title}</p>
            <p className="mt-1 text-[10px] text-[#C1A661] tracking-[0.28em] uppercase">EXPERTISE · CONFIANCE · PRÉCISION · INDÉPENDANCE</p>
            <p className="mt-3 text-[12px] text-[#A1A7B3] font-light max-w-2xl mx-auto">{t.subtitle}</p>
          </div>
          <div className="flex items-start gap-3 md:justify-end" data-testid="lab-trust-badge">
            <ShieldCheck size={22} className="text-[#C1A661] shrink-0 mt-0.5" strokeWidth={1.3} />
            <div className="max-w-[220px]">
              <div className="text-[10px] tracking-[0.25em] uppercase text-[#C1A661] font-medium">{t.trustBadge}</div>
            </div>
          </div>
        </div>

        {/* Labs list */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="labs-grid">
          {LABS.map((lab) => (
            <article key={lab.n} data-testid={`lab-${lab.n}`} className="border border-[#C1A661]/20 rounded-sm p-5 bg-[#080C17]">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-8 h-8 rounded-full border border-[#C1A661]/40 flex items-center justify-center text-[#C1A661] text-sm font-medium">{lab.n}</div>
                <div>
                  <div className="font-serif text-lg text-[#E2CFA4]">{lab.name}</div>
                  <div className="text-[10px] tracking-[0.2em] uppercase text-[#A1A7B3]">{lab.country}</div>
                </div>
              </div>
              <div className="space-y-2 text-[12px] text-[#D8DBE2] font-light">
                <div className="flex gap-2"><MapPin size={13} className="text-[#C1A661] mt-0.5 shrink-0" /><span className="whitespace-pre-line">{lab.addr}</span></div>
                <div className="flex gap-2 items-center"><Phone size={13} className="text-[#C1A661] shrink-0" /><span>{lab.phone}</span></div>
                <div className="flex gap-2 items-center"><Globe size={13} className="text-[#C1A661] shrink-0" /><span>{lab.web}</span></div>
              </div>
              <p className="mt-3 text-[11px] text-[#8A8F99] font-light italic">{lab.desc}</p>
            </article>
          ))}
        </div>

        {/* Why + Speciality */}
        <div className="grid md:grid-cols-2 gap-6">
          <div className="border border-[#C1A661]/20 rounded-sm p-6 bg-[#080C17]">
            <div className="text-center text-[10px] tracking-[0.3em] uppercase text-[#C1A661] mb-5">{t.why}</div>
            <div className="grid grid-cols-2 gap-4">
              {t.whyItems.map((it, i) => {
                const Icon = WHY_ICONS[i];
                return (
                  <div key={it.t} className="flex flex-col items-center text-center">
                    <Icon size={22} strokeWidth={1.3} className="text-[#C1A661]" />
                    <div className="mt-2 text-[11px] tracking-[0.2em] uppercase text-[#E2CFA4]">{it.t}</div>
                    <p className="mt-1 text-[10px] text-[#A1A7B3] font-light">{it.d}</p>
                  </div>
                );
              })}
            </div>
          </div>
          <div className="border border-[#C1A661]/20 rounded-sm p-6 bg-[#080C17]">
            <div className="text-center text-[10px] tracking-[0.3em] uppercase text-[#C1A661] mb-5">{t.speciality}</div>
            <div className="grid grid-cols-2 gap-4">
              {t.specialityItems.map((it, i) => {
                const Icon = SPEC_ICONS[i];
                return (
                  <div key={it.t} className="flex flex-col items-center text-center">
                    <Icon size={22} strokeWidth={1.3} className="text-[#C1A661]" />
                    <div className="mt-2 text-[11px] tracking-[0.2em] uppercase text-[#E2CFA4]">{it.t}</div>
                    <p className="mt-1 text-[10px] text-[#A1A7B3] font-light">{it.d}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Others */}
        <div>
          <div className="text-center text-[11px] tracking-[0.3em] uppercase text-[#C1A661] mb-4">{t.others}</div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            {OTHERS.map((o) => (
              <div key={o.name} className="border border-[#C1A661]/20 rounded-sm p-4 bg-[#080C17] text-[12px] text-[#D8DBE2] font-light">
                <div className="font-serif text-[#E2CFA4] text-lg mb-1">{o.name}</div>
                <div className="text-[10px] tracking-[0.2em] uppercase text-[#A1A7B3] mb-2">{o.loc}</div>
                <div className="whitespace-pre-line mb-2"><MapPin size={11} className="inline text-[#C1A661] mr-1" />{o.addr}</div>
                <div><Phone size={11} className="inline text-[#C1A661] mr-1" />{o.phone}</div>
                <div><Globe size={11} className="inline text-[#C1A661] mr-1" />{o.web}</div>
              </div>
            ))}
          </div>
        </div>

        <p className="text-center text-[11px] text-[#8A8F99] italic max-w-3xl mx-auto">{t.disclaimer}</p>

        <button onClick={() => nav("/")} data-testid="labs-subscribe-cta"
          className="w-full flex items-center justify-center gap-3 border border-[#C1A661]/40 hover:border-[#C1A661] py-4 rounded-sm transition-colors">
          <Lock size={14} className="text-[#C1A661]" />
          <span className="text-[11px] tracking-[0.25em] uppercase text-[#E2CFA4]">{t.ctaSubscribe}</span>
        </button>
      </div>
      <Footer />
    </div>
  );
};

export default LabsPage;
